import * as babylon from "@babylonjs/core";

import type { Camera } from "../../components/singletons";
import type {
  BabylonState,
  MeasureOverlayPoint,
  Vector3Plain,
} from "../../state";

/**
 * Projects a data-space point to canvas pixel coordinates. The point is
 * first shifted into render space (data minus the camera focus point, the
 * same frame `applyRenderOrigin` uses), then run through the frame's
 * view-projection.
 *
 * Points the camera cannot see still get coordinates. `visible` carries the
 * depth test; the consumer draws nothing where it is false.
 *
 * Call only after `scene.render`: the matrices are read off the scene, so
 * the projection agrees with the pixels the frame was rasterized with.
 */
export function projectDataPoint(
  state: BabylonState,
  camera: Camera,
  point: Vector3Plain,
): MeasureOverlayPoint {
  const render = new babylon.Vector3(
    point.x - camera.focusPoint.x,
    point.y - camera.focusPoint.y,
    point.z - camera.focusPoint.z,
  );
  const { engine, scene } = state.context;
  const projected = babylon.Vector3.Project(
    render,
    babylon.Matrix.IdentityReadOnly,
    scene.getTransformMatrix(),
    camera.babylonCamera.viewport.toGlobal(
      engine.getRenderWidth(),
      engine.getRenderHeight(),
    ),
  );
  const view = babylon.Vector3.TransformCoordinates(
    render,
    scene.getViewMatrix(),
  );
  return {
    screenX: projected.x,
    screenY: projected.y,
    // View space is left-handed (nothing sets `useRightHandedSystem`), so
    // points in front of the camera have positive z. Test against the near
    // plane, not zero: between the two the perspective divide is by a
    // near-zero w, which projects a clipped point to huge screen coordinates
    // and would fling a rubberband across the panel while an anchor crosses
    // the camera plane.
    visible: view.z > camera.babylonCamera.minZ,
  };
}
